import React, { useContext, useEffect } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { Grid, Header, Segment } from 'semantic-ui-react'
import { FormFields } from '~/components/FormFields'
import { CoolFarmCropOptionsContext } from '../../index'
import { useTranslation } from 'react-i18next'
import InfoPopup from '~/components/InfoPopup'

const CUSTOM_ORGANIC_MATTER_KEY = 5

const Soil: React.FC = () => {
  const { t } = useTranslation()
  const { control, setValue } = useFormContext()
  const options = useContext(CoolFarmCropOptionsContext)

  const watchSoilOrganicValue = useWatch({ control, name: 'soilOrganicMatterValue' })

  useEffect(() => {
    if(watchSoilOrganicValue !== CUSTOM_ORGANIC_MATTER_KEY) {
      setValue('soilOrganicMatterCustomValue', '')
      setValue('soilOrganicMatterMeasuredYearsAgo', '')
    }
  }, [watchSoilOrganicValue])

  const soilTextureOptions = options?.soilTextures?.map(texture => ({
    key: texture.key,
    value: texture.key,
    text: texture.label
  })) || []

  const soilOrganicMatterOptions = options?.soilOrganicMatterValues?.map(matter => ({
    key: matter.key,
    value: matter.key,
    text: matter.label
  })) || []

  const soilDrainageOptions = options?.soilDrainageTypes?.map(drainage => ({
    key: drainage.key,
    value: drainage.key,
    text: drainage.label
  })) || []

  const soilPhOptions = options?.soilPhValues?.map(ph => ({
    key: ph.key,
    value: ph.key,
    text: ph.label
  })) || []

  const isCustomOrganicValue = watchSoilOrganicValue === CUSTOM_ORGANIC_MATTER_KEY

  return (
    <Grid>
      <Grid.Row>
        <Grid.Column width={16}>
         
          <Grid>
            <Grid.Row>
              <Grid.Column width={8}>
                <FormFields.Select
                  control={control}
                  name='soilTexture'
                  label={<div style={{ display: 'flex', gap: 5 }}>
                    <label>{t('Soil texture')}</label>
                    <InfoPopup
                      content={t('"Coarse" includes sand, loamy sand, sandy loam, loam, silt loam and silt. "Medium" includes sandy clay loam, clay loam and silty clay loam. "Fine" includes sandy clay, silty clay and clay."')}
                      styleIcon={{ fontSize: '1.2em' }}
                      />
                  </div>}
                  withoutAsterisk
                  options={soilTextureOptions}
                  placeholder={t('Select soil texture')}
                  noErrorMessage
                  />
              </Grid.Column>
              <Grid.Column width={8}>
                <FormFields.Select
                  control={control}
                  name='soilOrganicMatterValue'
                  label={<div style={{ display: 'flex', gap: 5 }}>
                    <label>{t('Soil organic matter')}</label>
                    <InfoPopup
                      style={{ minWidth: 400 }}
                      content={t(`Enter the soil organic matter (SoM) from an assessment, or ideally from a recent lab test on a sample taken from 0-30cm. Enter the custom value if you know it, otherwise use the drop-down ranges.
                            Soil organic carbon value is calculated from SoM."Custom value" and "Measured years ago" are used together on the SOC model to calculate emissions from land use change and populating these fields will give a more accurate result.`)}
                      styleIcon={{ fontSize: '1.2em' }}
                      />
                  </div>}
                  withoutAsterisk
                  options={soilOrganicMatterOptions}
                  placeholder={t('Select soil organic matter')}
                  noErrorMessage
                  />
              </Grid.Column>
            </Grid.Row>

            {isCustomOrganicValue && <Grid.Row>
              <Grid.Column width={8} >
                <FormFields.Input
                  control={control}
                  name='soilOrganicMatterCustomValue'
                  withoutAsterisk
                  rules={{
                    required: t('Please enter soil organic amount'),
                    min: {
                      value: 0,
                      message: t('Min. value is 0')
                    },
                    max: {
                      value: 30,
                      message: t('Max value is 30')
                    }
                  }}
                  label={t('Soil organic matter custom')}
                  placeholder={t('Enter soil organic amount')}
                  typeInput='controled'
                  />
              </Grid.Column>
              <Grid.Column width={8}>
                <FormFields.Input
                  control={control}
                  name='soilOrganicMatterMeasuredYearsAgo'
                  withoutAsterisk
                  rules={{
                    required: true,
                    min: {
                      value: 0,
                      message: t('Min. value is 0')
                    }
                  }}
                  label={t('Measured years ago')}
                  placeholder={t('Enter years ago')}
                  typeInput='controled'
                  noErrorMessage
                  />
              </Grid.Column>
              </Grid.Row>}

            <Grid.Row>
              <Grid.Column width={8}>
                <FormFields.Select
                  control={control}
                  name='soilDrainageType'
                  label={<div style={{ display: 'flex', gap: 5 }}>
                    <label>{t('Soil drainage type')}</label>
                    <InfoPopup
                      style={{ minWidth: 400 }}
                      content={t(`Soils which are often saturated or show surface water, should be classified 'Poor'. 
                          This would usually include clay soils with restricted or no drainage. For other cases enter 'Good'.Soil drainage mainly affects nitrous oxide (N2O) emissions from the soil.`)}
                      styleIcon={{ fontSize: '1.2em' }}
                      />
                  </div>}
                  withoutAsterisk
                  options={soilDrainageOptions}
                  placeholder={t('Select soil drainage type')}
                  noErrorMessage
                  />
              </Grid.Column>
              <Grid.Column width={8}>
                <FormFields.Select
                  control={control}
                  name='soilPhValue'
                  label={<div style={{ display: 'flex', gap: 5 }}>
                    <label>{t('Soil pH value')}</label>
                    <InfoPopup
                      style={{ minWidth: 400 }}
                      content={t('Input here the average pH from an in field or lab test. If you don\'t have this information, enter 5.5-7.3 which is neutral pH.')}
                      styleIcon={{ fontSize: '1.2em' }}
                      />
                  </div>}
                  withoutAsterisk
                  options={soilPhOptions}
                  placeholder={t('Select soil pH value')}
                  noErrorMessage
                  />
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </Grid.Column>
      </Grid.Row>
    </Grid>
  )
}

export default Soil
